import {ChangeDetectorRef, Component, DoCheck, Input, OnInit} from '@angular/core';
import {PlanModelService} from '../../common/service/plan-model.service';
import {LookupService} from '../../common/service/lookup.service';
import {Road} from '../../common/models/road.model';
import {NgEventBus} from 'ng-event-bus';
import {PDF_CREATION_METHOD} from '../../common/constants';
import {NgClass} from '@angular/common';
import {ControlContainer, FormsModule, NgForm, ReactiveFormsModule} from '@angular/forms';
import {debounceTime, distinctUntilChanged, forkJoin, map, Observable, of} from 'rxjs';
import {NlrTypeheadDirective} from '../../common/directive/nlr-typehead.directive';
import {NlrHasPermissionsPipe} from '../../common/pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../common/pipe/nlr-permissions-disabled-pipe';
import {PlanLocality} from '../../common/models/plan-locality.model';
import {NlrPatternDirective} from '../../common/directive/nlr-pattern.directive';
import {NlrFocusDirective} from '../../common/directive/nlr-focus.directive';
import {Locality} from '../../common/models/locality.model';
import {PlanRoad} from '../../common/models/plan-road.model';
import {PermissionsService} from '../../common/service/permissions.service';
import {NlrUppercaseDirective} from '../../common/directive/nlr-uppercase.directive';
import {NgSelectModule} from '@ng-select/ng-select';

@Component({
    selector: 'nlr-road',
    imports: [
        ReactiveFormsModule,
        FormsModule,
        NlrTypeheadDirective,
        NgClass,
        NlrHasPermissionsPipe,
        NlrPermissionsDisabledPipe,
        NlrPatternDirective,
        NlrFocusDirective,
        NlrUppercaseDirective,
        NgSelectModule
    ],
    templateUrl: './road.component.html',
    styleUrl: './road.component.scss',
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class RoadComponent implements OnInit, DoCheck {
    @Input() submitted: boolean;
    get readOnly() {
        return this.planModelService.readonly;
    }
    roadsLookupList = undefined;
    private selectedRoadIndex: number;
    validCharacters = /^[a-zA-Z0-9 ]+$/;
    private allLocalities: Locality[];

    constructor(
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private permissionsService: PermissionsService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.initialiseLookups();
    }

    private initialiseLookups() {
        this.roadsLookupList = this.lookupService.getLookups('roadType');
        this.planPurposeList = this.lookupService.getLookups('planPurpose');
        this.getAllLocalities();
    }

    getAllLocalities() {
        const subs = this.lookupService.retrieveLookup<Locality[]>('/locationname/findAllLocalities').subscribe({
            next: data => {
                this.allLocalities = data;
                console.log('Localities load success ');
            },
            error: error => {
                console.error('Localities Load failed ' + error.message);
            }
        });
        this.eventBus.cast('nlr:busy', subs);
    }

    title = 'Road/s';
    roads: Road[][] = [];
    localities: PlanLocality[] = [];
    isRoadNamesModified = false;
    planPurposeList = [];

    laaAcquisitionValues = [
        {value: true, displayValue: 'Yes', active: true},
        {value: false, displayValue: 'No', active: true}
    ];

    roadClosureValues = [
        {value: true, displayValue: 'Yes', active: true},
        {value: false, displayValue: 'No', active: true}
    ];

    roadWideningValues = [
        {value: true, displayValue: 'Yes', active: true},
        {value: false, displayValue: 'No', active: true}
    ];

    newOrExtendedRoadsValues = [
        {value: true, displayValue: 'Yes', active: true},
        {value: false, displayValue: 'No', active: true}
    ];

    lgaApprovedUnnamedValues = [
        {value: true, displayValue: 'Yes', active: true},
        {value: false, displayValue: 'No', active: true}
    ];

    approvalExemptionValues = [
        {value: true, displayValue: 'Yes', active: true},
        {value: false, displayValue: 'No', active: true}
    ];

    getModel() {
        return this.planModelService.getModel();
    }

    getLocalities() {
        return this.getModel().localities?.filter(locality => locality.localityName) || [];
    }

    localityTrackBy(item) {
        return item.localityName;
    }

    newOrExtendedRoadsChange(newOrExtendedRoads: boolean) {
        console.debug(`${this.title} - newOrExtendedRoadsChange - newOrExtendedRoads: ${newOrExtendedRoads}`);

        const planModel = this.getModel();

        if (newOrExtendedRoads) {
            planModel.roads = [];
            this.roads = [];
            this.addRow();
            this.setRoadDefaultLocality(0);

            if (planModel.roads[0].localityName) {
                const subs = this.lookupService
                    .retrieveLookup<Road[]>('/locationname/findAllRoadsByLocality/' + planModel.roads[0].localityName)
                    .subscribe({
                        next: data => {
                            this.roads[0] = data;
                        },
                        error: error => {
                            console.error('Road search failed ' + error.message);
                        }
                    });
                this.eventBus.cast('nlr:busy', subs);
            }
        } else {
            planModel.roads = [];
            this.roads = [];
            if (planModel.attachments) {
                const roadNameApprovalIdx = planModel.attachments.findIndex(a => a.attachmentType === 'RD_APPRVL');
                if (roadNameApprovalIdx !== -1) {
                    planModel.attachments.splice(roadNameApprovalIdx, 1);
                }
            }
        }
        this.eventBus.cast('updateAttachmentStatus');
    }

    setRoadDefaultLocality(roadIndex) {
        if (this.getModel().localities !== undefined && this.getModel().localities.length === 1) {
            this.getModel().roads[roadIndex].localityName = this.getModel().localities[0].localityName;
        } else {
            this.getModel().roads[roadIndex].localityName = undefined;
        }
    }

    localityChange(index: number, locality: PlanLocality) {
        console.debug(this.title + ' localityChange');
        if (locality.localityName === this.getModel().roads[index].localityName) {
            return;
        }
        this.getModel().roads[index].road = this.newRoad();
        this.getModel().roads[index].localityName = locality.localityName;

        // Retrieve roads by locality
        const subs = this.lookupService.retrieveLookup<Road[]>('/locationname/findAllRoadsByLocality/' + locality.localityName).subscribe({
            next: data => {
                this.roads[index] = data;
            },
            error: error => {
                console.error('Road search failed ' + error.message);
            }
        });
        this.eventBus.cast('nlr:busy', subs);
    }

    approvalExemptionChange(approvalExemption: boolean) {
        console.debug(`${this.title} - approvalExemptionChange - approvalExemption: ${approvalExemption}`);

        const planModel = this.getModel();

        // ND-54: leave the field for a new road exemption as empty on the form after saving a plan if nothing is entered.
        if (approvalExemption) {
            if (planModel.exemptedRoads.length === 0) {
                planModel.exemptedRoads.push({name: ''});
            }
        } else {
            planModel.exemptedRoads.length = 0;
            if (planModel.attachments) {
                const roadNameApprovalExemptionIdx = planModel.attachments.findIndex(a => a.attachmentType === 'RD_EXEMPT');
                if (roadNameApprovalExemptionIdx !== -1) {
                    planModel.attachments.splice(roadNameApprovalExemptionIdx, 1);
                }
            }
        }
        this.eventBus.cast('updateAttachmentStatus');
    }

    newRoad(): Road {
        return {
            displayName: '',
            roadName: '',
            roadTypeCode: '',
            roadTypeDescription: '',
            roadSuffixDescription: ''
        };
    }
    addRow() {
        console.debug(this.title + ' controller addRow');

        this.getModel().roads.push({road: this.newRoad(), localityName: ''});

        const roadIndex = this.getModel().roads.length - 1;
        this.setRoadDefaultLocality(roadIndex);

        if (this.getModel().roads[0].localityName !== undefined && this.getModel().roads[0].localityName !== '') {
            // Retrieve roads by locality
            const localityName = this.getModel().roads[roadIndex].localityName;
            const subs = this.lookupService.retrieveLookup<Road[]>('/locationname/findAllRoadsByLocality/' + localityName).subscribe({
                next: data => {
                    this.roads.push(data);
                },
                error: error => {
                    console.error('Road search failed ' + error.message);
                }
            });
            this.eventBus.cast('nlr:busy', subs);
        }
    }

    removeRow(i: number) {
        console.debug(this.title + ' controller removeRow');

        this.getModel().roads = this.getModel().roads.filter((row, index) => index !== i);

        this.roads = this.roads.filter((row, index) => index !== i);
    }

    getRoads(roadElement, viewValue) {
        console.debug(this.title + ' controller getRoads');

        const roads = this.roads[roadElement.$index] || [];
        return roads.filter(
            r => r.displayName.toLowerCase().startsWith(viewValue.toLowerCase()) && !this.hasSameRoadDisplayName(r, this.selectedRoadIndex)
        );
    }

    roadsSearch: (text$: Observable<string>) => Observable<string[]> = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            map(term =>
                term.length < 1
                    ? []
                    : this.roads[this.selectedRoadIndex]
                          .filter(
                              r =>
                                  r.displayName.toLowerCase().indexOf(term.toLowerCase()) > -1 &&
                                  !this.hasSameRoadDisplayName(r, this.selectedRoadIndex)
                          )
                          .map(r => r.displayName)
                          .slice(0, 10)
            )
        );

    hasSameRoadDisplayName(road: Road, index: number) {
        const location = this.getModel().roads[index].localityName;
        return this.getModel().roads.filter(r => r.road.displayName === road.displayName && r.localityName === location).length > 1;
    }

    isRoadDuplicate(index: number) {
        const road = this.getModel().roads[index];
        if (!road.road.displayName) {
            return false;
        }
        return (
            this.getModel().roads.filter(
                r => r.road.displayName && r.road.displayName === road.road.displayName && r.localityName === road.localityName
            ).length > 1 || this.getModel().exemptedRoads.filter(r => road.road.displayName === r.name).length > 0
        );
    }

    isExemptRoadDuplicate(name: string) {
        if (!name) {
            return false;
        }
        if (name === this.getRoadWideningPlanLabel() || name === this.getLgaApprovedUnnamedRoadPlanLabel()) {
            return true;
        }
        return (
            [...this.getModel().roads.map(r => r.road.displayName), ...this.getModel().exemptedRoads.map(r => r.name)].filter(s => s === name)
                .length > 1
        );
    }

    roadChanged(elementIndex) {
        console.debug(elementIndex);
    }

    roadSelected(item) {
        console.log('item ', item);
        const index = this.getModel().roads.findIndex(r => r.road.displayName === item);
        const road = this.roads[index].find(r => r.displayName === item);
        this.getModel().roads[index].road = structuredClone(road);
    }

    exemptedRoadNameChanged() {
        this.isRoadNamesModified = true;
    }

    addExemptedRoadName() {
        console.debug(this.title + ' controller addExemptedRoadName');

        this.getModel().exemptedRoads.push({name: ''});
    }

    removeExemptedRoadName(i: number) {
        console.debug(this.title + ' controller removeExemptedRoadName');
        this.getModel().exemptedRoads.splice(i, 1);
    }

    getLocalityName(road) {
        return road !== undefined ? road.localityName : undefined;
    }

    isRoadLocalityValid(localityName: string) {
        if (!this.localities || this.localities.length === 0) {
            return false;
        }
        return this.localities.some(l => l.localityName === localityName);
    }

    isCadPlan() {
        return this.getModel().pdfCreationMethod === PDF_CREATION_METHOD.CAD_SOFTWARE;
    }

    isDigitalPlan() {
        return this.getModel().pdfCreationMethod === PDF_CREATION_METHOD.DIGITAL_PLAN;
    }

    isPortalPlan() {
        return this.getModel().pdfCreationMethod === PDF_CREATION_METHOD.SURVEYOR_PORTAL;
    }

    getRoadWideningPlanLabel() {
        if ((this.isDigitalPlan() || this.isPortalPlan()) && this.getModel().roadWidening) {
            return this.roadsLookupList.find(road => road.code === 'ROAD_WIDENING').defaultRoadName;
        }

        return null;
    }

    getLgaApprovedUnnamedRoadPlanLabel() {
        if ((this.isDigitalPlan() || this.isPortalPlan()) && this.getModel().lgaApprovedUnnamedRoad) {
            return this.roadsLookupList.find(road => road.code === 'ROAD_LGA').defaultRoadName;
        }

        return null;
    }

    isValidLocality(locName: string) {
        if (!this.allLocalities) return true;
        return this.allLocalities.some(l => l.name === locName);
    }

    isValidRoadName(road: PlanRoad, index: number) {
        return (
            road.localityName &&
            this.isRoadLocalityValid(road.localityName) &&
            index < this.roads.length &&
            this.roads[index].some(r => r.displayName === road.road.displayName)
        );
    }

    ngDoCheck() {
        const {roads, exemptedRoads, approvalExemption, newOrExtendedRoads} = this.getModel();

        if (newOrExtendedRoads && roads.length === 0) {
            roads.push(<PlanRoad>{localityName: '', road: this.newRoad()});
        }

        if (approvalExemption && exemptedRoads.length === 0) {
            exemptedRoads.push({name: ''});
        }

        if (this.isCadPlan() && exemptedRoads.length) {
            // CAD plan exempted roads should only contain one empty object which is removed when model is sanitised
            if (exemptedRoads.length > 1) {
                exemptedRoads.splice(0, exemptedRoads.length, {});
            }

            if (Object.keys(exemptedRoads[0]).length) {
                exemptedRoads[0] = {};
            }
        }

        if (
            this.localities.length !== this.getModel().localities.length ||
            this.localities.some(
                (item, index) =>
                    item.localityName !== this.getModel().localities[index].localityName &&
                    this.isValidLocality(this.getModel().localities[index].localityName)
            )
        ) {
            this.localities = structuredClone(this.getModel().localities);

            const roadsRequests = roads.map(road => {
                if (this.isValidLocality(road.localityName) && this.getLocalities().findIndex(l => l.localityName === road.localityName) >= 0) {
                    return this.lookupService.retrieveLookup(`/locationname/findAllRoadsByLocality/${road.localityName}`);
                } else {
                    road.road = this.newRoad();
                    road.localityName = '';
                    return of([]);
                }
            });
            forkJoin(roadsRequests)
                .pipe(map(responses => (this.roads = responses as Road[][])))
                .subscribe();
        }

        if (
            this.getModel().localities &&
            this.getModel().localities.length === 1 &&
            this.getModel().localities[0].localityName &&
            newOrExtendedRoads &&
            !roads[0].localityName
        ) {
            this.setRoadDefaultLocality(0);
            const subs = this.lookupService.retrieveLookup<Road[]>(`/locationname/findAllRoadsByLocality/${roads[0].localityName}`).subscribe({
                next: data => {
                    this.roads[0] = data;
                },
                error: error => {
                    console.error('Road search failed ' + error.message);
                }
            });
            this.eventBus.cast('nlr:busy', subs);
        }

        this.cdr.detectChanges();
    }

    planDetailsVerification(p) {
        const model = this.getModel();
        if (!model.tenureType || !model.planType || !model.planPurpose) {
            return false;
        }
        return p.id === model.planPurpose.id && p.tenureType === model.tenureType.name && p.planType === model.planType.name;
    }

    isPrivateRoadQuestion() {
        return this.planPurposeList.some(p => p.privateRoadQuestion && this.planDetailsVerification(p));
    }

    isRoadClosureQuestion() {
        return this.planPurposeList.some(p => p.roadClosureQuestion && this.planDetailsVerification(p));
    }

    isRoadWideningQuestion() {
        return this.planPurposeList.some(p => p.roadWideningQuestion && this.planDetailsVerification(p));
    }

    isRoadNameApprovalQuestion() {
        return this.planPurposeList.some(p => p.roadNameApprovalQuestion && this.planDetailsVerification(p));
    }

    isRoadNameApprovalExemptQuestion() {
        return this.planPurposeList.some(p => p.roadNameApprovalExemptQuestion && this.planDetailsVerification(p));
    }

    isLgaApprovedUnnamedRoadQuestion() {
        return this.planPurposeList.some(p => p.lgaApprovedUnnamedRoadQuestion && this.planDetailsVerification(p));
    }

    oneSpaceCheck(i: number) {
        const spacesInMiddle = /\s{2,}/;
        return spacesInMiddle.test(this.getModel().exemptedRoads[i].name);
    }

    private blurRoadNameTimeout: number;

    onFocusRoadName(i: number) {
        this.selectedRoadIndex = i;
        if (this.blurRoadNameTimeout) {
            clearTimeout(this.blurRoadNameTimeout);
            this.blurRoadNameTimeout = null;
        }
    }

    onBlurRoadName(road: PlanRoad, i: number) {
        this.blurRoadNameTimeout = setTimeout(() => {
            if (this.roads.length === this.getModel().roads.length && road.road.displayName && !this.isValidRoadName(road, i)) {
                road.road.displayName = '';
            }
        }, 500);
    }

    showExemptRoad() {
        return (
            (this.isDigitalPlan() || this.isPortalPlan()) &&
            this.getModel().approvalExemption &&
            this.permissionsService.hasPermission(['EDIT_INFORMATION'])
        );
    }
}
